import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomButton from '../reusable/CustomButton';
import CustomTable, { TableHeaderData } from '../reusable/CustomTable';
import Loader from '../reusable/Loader';
import TableSkeleton from '../reusable/TableSkeleton';
import Panel from '../reusable/Panel';
import PageHeader from '../reusable/PageHeader';
import useUserDetails from '../hooks/useUserDetails';
import useSchools from '../hooks/useSchools';
import { getConfiguredSchools, getEOAConfig } from '../../services/eoaConfigServices';
import { getWorkflowStatuses } from '../../services/workflowService';
import { ConfiguredEOASchool } from '../../interfaces/EOAInterface';
import { USER_ROLES } from '../../constants';
import TranscriptSelectionStatus from './TranscriptSelectionStatus';
import GoBackIcon from '../assets/transcript-selection-arrow-left.svg';
import './TranscriptSelectionsOverview.css';
import { formatDate } from '../../utils';

interface SchoolWithStatus {
  id: string;
  name: string;
  status: string;
  dueDate: string;
}

const tableHeaderData: TableHeaderData<SchoolWithStatus>[] = [
  { key: 'name', label: 'School', icons: ['sort'] },
  { key: 'status', label: 'Status', icons: ['sort'] },
  { key: 'dueDate', label: 'Due date' },
  { key: 'actions', label: 'Actions' },
];

const TranscriptSelectionsOverview = () => {
  const navigate = useNavigate();
  const {
    userDetails: { data: userDetails },
  } = useUserDetails();
  const { updateTranscriptSelectionSchool } = useSchools();

  const [loading, setLoading] = useState(true);
  const [schools, setSchools] = useState<SchoolWithStatus[]>([]);

  useEffect(() => {
    const fetchSchoolsAndStatus = async () => {
      if (!userDetails) return;

      try {
        setLoading(true);

        const [eoaConfigResponse, schoolsResponse, workflowStatusesResponse] = await Promise.all([
          getEOAConfig(userDetails.eoaId, userDetails.access_token),
          getConfiguredSchools(userDetails.eoaId, userDetails.access_token),
          getWorkflowStatuses(userDetails.eoaId, userDetails.access_token).catch(() => ({
            schools: {} as Record<string, { transcript_selection: string | null }>,
          })),
        ]);

        const sjwDate = eoaConfigResponse.data.sjw_date;

        const formattedDueDate = formatDate(sjwDate);

        const configuredSchools: ConfiguredEOASchool[] = schoolsResponse.data;
        const primarySchools = configuredSchools.filter((school) => school.is_primary_on_eoa !== false);
        const workflowStatuses = workflowStatusesResponse.schools;

        const schoolsWithStatus = primarySchools.map((school: ConfiguredEOASchool) => {
          const schoolWorkflowStatus = workflowStatuses[school.id];
          const status = schoolWorkflowStatus?.transcript_selection || 'DATA_QC_NOT_COMPLETED';

          return {
            id: school.id,
            name: school.name,
            status,
            dueDate: formattedDueDate,
          };
        });

        setSchools(schoolsWithStatus);
      } catch (error) {
        console.error('Error fetching schools and status:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSchoolsAndStatus();
  }, [userDetails]);

  const handleNavigateToTranscripts = (schoolId: string) => {
    updateTranscriptSelectionSchool(schoolId);
    navigate('/transcripts');
  };

  const renderActionButton = (school: SchoolWithStatus) => {
    const userRole = userDetails?.role;
    const isLeadFacilitator = userRole === USER_ROLES.LEAD_FACILITATOR;
    const isLeadFacilitatorOrAdmin = userRole === USER_ROLES.XQ_ADMIN || userRole === USER_ROLES.LEAD_FACILITATOR;
    const isFacilitator = userRole === USER_ROLES.FACILITATOR;

    if (isLeadFacilitatorOrAdmin && school.status === 'TRANSCRIPT_SELECTION_COMPLETED') {
      return (
        <CustomButton
          variant="primary"
          size="small"
          buttonText="Review"
          buttonAction={() => handleNavigateToTranscripts(school.id)}
        />
      );
    }

    if (
      isFacilitator &&
      (school.status === 'TRANSCRIPT_SELECTION_IN_PROGRESS' || school.status === 'TRANSCRIPT_SELECTION_NOT_STARTED')
    ) {
      return (
        <CustomButton
          variant="primary"
          size="small"
          buttonText="Edit"
          buttonAction={() => handleNavigateToTranscripts(school.id)}
        />
      );
    }

    if (
      isLeadFacilitator &&
      (school.status === 'TRANSCRIPT_SELECTION_IN_PROGRESS' || school.status === 'TRANSCRIPT_SELECTION_NOT_STARTED')
    ) {
      return (
        <CustomButton
          variant="text"
          size="small"
          buttonText="View"
          buttonAction={() => handleNavigateToTranscripts(school.id)}
        />
      );
    }

    if (isFacilitator && school.status === 'TRANSCRIPT_SELECTION_COMPLETED') {
      return (
        <CustomButton
          variant="text"
          size="small"
          buttonText="View"
          buttonAction={() => handleNavigateToTranscripts(school.id)}
        />
      );
    }

    if ((isFacilitator || isLeadFacilitatorOrAdmin) && school.status === 'TRANSCRIPT_SELECTION_FINALIZED') {
      return (
        <CustomButton
          variant="text"
          size="small"
          buttonText="View"
          buttonAction={() => handleNavigateToTranscripts(school.id)}
        />
      );
    }

    return <span></span>;
  };

  if (!userDetails) return <Loader />;

  const userRole = userDetails?.role;
  const isLeadFacilitatorOrAdmin = userRole === USER_ROLES.XQ_ADMIN || userRole === USER_ROLES.LEAD_FACILITATOR;
  const isFacilitator = userRole === USER_ROLES.FACILITATOR;

  return (
    <>
      {isFacilitator && (
        <PageHeader
          title="Transcript selection"
          pageDescription="Select student transcripts to explore data and gather insights"
        />
      )}
      <div className="main-content">
        {isLeadFacilitatorOrAdmin && (
          <>
            <CustomButton
              variant="text"
              buttonText="Back"
              icon={<img src={GoBackIcon} alt="go back" />}
              iconPosition="start"
              buttonAction={() => navigate('/overview')}
            />
            <h1 className="transcript-selections-page-title">Transcript selection</h1>
          </>
        )}
        <Panel className="transcript-selections-overview-panel">
          <h2 className="transcript-selections-panel-title">{isFacilitator ? 'Select a school' : 'Progress status'}</h2>
          {isFacilitator && (
            <p className="transcript-selections-panel-description">
              Choose the school from which to review and select student transcripts for EOAA reports.
            </p>
          )}
          <CustomTable
            tableHeaderData={tableHeaderData.map((header) => {
              if (header.key === 'status') {
                return {
                  ...header,
                  renderCell: (row: SchoolWithStatus) => <TranscriptSelectionStatus status={row.status} />,
                };
              }
              if (header.key === 'actions') {
                return {
                  ...header,
                  renderCell: (row: SchoolWithStatus) => renderActionButton(row),
                };
              }
              return header;
            })}
            tableBodyData={loading ? undefined : schools}
          >
            {loading && <TableSkeleton rowsNumber={3} colsNumber={tableHeaderData.length} />}
          </CustomTable>
        </Panel>
      </div>
    </>
  );
};

export default TranscriptSelectionsOverview;
